/**
 * CESM OSINT System - Premium Sources Panel
 * Interface de gestion des abonnements presse premium
 * 
 * Concept & Développement: CDT (R) Jean-Baptiste GIRAUD
 * LPP Logiciel © 2026
 */
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  X, Key, Newspaper, Shield, Ship, Zap, Globe,
  Eye, EyeOff, Save, Trash2, CheckCircle2, AlertCircle,
  ExternalLink, Loader2, Lock, Unlock
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '@/App';
import { addGlobalLog } from './GlobalConsole';

// Configuration des sources premium
const PREMIUM_SOURCES = [
  {
    id: "financial_times",
    name: "Financial Times",
    category: "géopolitique",
    icon: Globe,
    url: "https://www.ft.com/",
    description: "Analyses économiques et géopolitiques de référence",
    accessLevel: "Titres + résumés (abonnement: contenu complet)",
    color: "text-pink-400",
    bgColor: "bg-pink-900/20",
    borderColor: "border-pink-500/50"
  },
  {
    id: "economist",
    name: "The Economist",
    category: "géopolitique",
    icon: Globe,
    url: "https://www.economist.com/",
    description: "Analyses hebdomadaires monde, économie, politique",
    accessLevel: "Titres uniquement (abonnement: articles)",
    color: "text-red-400",
    bgColor: "bg-red-900/20",
    borderColor: "border-red-500/50"
  },
  {
    id: "janes_premium",
    name: "Janes Intelligence",
    category: "défense",
    icon: Shield,
    url: "https://www.janes.com/",
    description: "Renseignement défense et capacités militaires",
    accessLevel: "Non connecté - Critique pour analyse défense",
    color: "text-orange-400",
    bgColor: "bg-orange-900/20",
    borderColor: "border-orange-500/50",
    critical: true
  },
  {
    id: "lloyds_premium",
    name: "Lloyd's List Intelligence",
    category: "maritime",
    icon: Ship,
    url: "https://lloydslist.maritimeintelligence.informa.com/",
    description: "Données maritimes, mouvements de navires, fret",
    accessLevel: "RSS basique (premium: données temps réel)",
    color: "text-blue-400",
    bgColor: "bg-blue-900/20",
    borderColor: "border-blue-500/50"
  },
  {
    id: "platts_premium",
    name: "S&P Global Platts",
    category: "énergie",
    icon: Zap,
    url: "https://www.spglobal.com/commodityinsights/",
    description: "Prix de référence pétrole, gaz, électricité",
    accessLevel: "Données partielles (premium: temps réel)",
    color: "text-yellow-400",
    bgColor: "bg-yellow-900/20",
    borderColor: "border-yellow-500/50"
  },
  {
    id: "argus_media",
    name: "Argus Media",
    category: "énergie",
    icon: Zap,
    url: "https://www.argusmedia.com/",
    description: "Prix et analyses marchés énergétiques",
    accessLevel: "Non connecté",
    color: "text-green-400",
    bgColor: "bg-green-900/20",
    borderColor: "border-green-500/50"
  },
  {
    id: "meta_defense",
    name: "Meta-Défense",
    category: "défense FR",
    icon: Shield,
    url: "https://meta-defense.fr/",
    description: "Analyses défense françaises et européennes",
    accessLevel: "Articles publics + premium",
    color: "text-cyan-400",
    bgColor: "bg-cyan-900/20",
    borderColor: "border-cyan-500/50"
  },
];

const PremiumSourcesPanel = ({ token, isOpen, onClose }) => {
  const [credentials, setCredentials] = useState({});
  const [showPassword, setShowPassword] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState({});
  const [savedSources, setSavedSources] = useState([]);

  // Charger les credentials existants
  useEffect(() => {
    if (isOpen && token) {
      loadCredentials();
    }
  }, [isOpen, token]);

  const loadCredentials = async () => {
    setIsLoading(true);
    try {
      const res = await axios.get(`${API}/api/sources/all`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Identifier les sources avec credentials
      if (res.data.premium_sources) {
        const saved = res.data.premium_sources
          .filter(s => s.has_credentials)
          .map(s => s.id);
        setSavedSources(saved);
      }
    } catch (err) {
      console.error('Error loading credentials:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (sourceId, field, value) => {
    setCredentials(prev => ({
      ...prev,
      [sourceId]: {
        ...prev[sourceId],
        [field]: value
      }
    }));
  };

  const handleSaveCredential = async (sourceId) => {
    const cred = credentials[sourceId];
    if (!cred?.username || !cred?.password) {
      toast.error('Veuillez remplir tous les champs');
      return;
    }

    setIsSaving(prev => ({ ...prev, [sourceId]: true }));
    
    try {
      await axios.post(`${API}/api/sources/credentials`, {
        [sourceId]: {
          username: cred.username,
          password: cred.password
        }
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setSavedSources(prev => [...prev, sourceId]);
      addGlobalLog('SOURCES', `Credentials sauvegardés pour ${sourceId}`);
      toast.success(`Credentials sauvegardés pour ${PREMIUM_SOURCES.find(s => s.id === sourceId)?.name}`);
    } catch (err) {
      console.error('Error saving credentials:', err);
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setIsSaving(prev => ({ ...prev, [sourceId]: false }));
    }
  };

  const handleDeleteCredential = async (sourceId) => {
    try {
      await axios.delete(`${API}/api/sources/credentials/${sourceId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setSavedSources(prev => prev.filter(id => id !== sourceId));
      setCredentials(prev => {
        const newCreds = { ...prev };
        delete newCreds[sourceId];
        return newCreds;
      });
      
      addGlobalLog('SOURCES', `Credentials supprimés pour ${sourceId}`);
      toast.success('Credentials supprimés');
    } catch (err) {
      console.error('Error deleting credentials:', err);
      toast.error('Erreur lors de la suppression');
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          className="bg-marine-navy border-2 border-marine-amber/50 rounded-lg w-full max-w-4xl max-h-[85vh] overflow-hidden"
          onClick={e => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-marine-amber/30 bg-gradient-to-r from-amber-900/30 to-black">
            <div className="flex items-center gap-3">
              <Key className="w-6 h-6 text-marine-amber" />
              <div>
                <h2 className="font-heading text-marine-amber text-xl tracking-wider">
                  SOURCES PREMIUM
                </h2>
                <p className="font-mono text-white/80 text-sm">
                  Gestion des abonnements presse
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-white/80 hover:text-white hover:bg-white/10 rounded"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Content */}
          <div className="overflow-y-auto max-h-[65vh] p-4">
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 text-marine-amber animate-spin" />
              </div>
            ) : (
              <div className="space-y-4">
                {/* Info Banner */}
                <div className="p-3 bg-marine-cyan/10 border border-marine-cyan/30 rounded flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-marine-cyan flex-shrink-0 mt-0.5" />
                  <div className="font-mono text-sm text-white/80">
                    <strong>Note:</strong> Ces credentials sont stockés de manière sécurisée et utilisés uniquement 
                    pour accéder aux contenus premium lors des scans OSINT. Vos identifiants ne sont jamais partagés.
                  </div>
                </div>

                {/* Sources List */}
                {PREMIUM_SOURCES.map(source => {
                  const isConnected = savedSources.includes(source.id);
                  const cred = credentials[source.id] || {};
                  const IconComponent = source.icon;
                  
                  return (
                    <div 
                      key={source.id}
                      className={`p-4 border rounded-lg ${source.bgColor} ${source.borderColor} ${source.critical ? 'ring-2 ring-red-500/50' : ''}`}
                    >
                      {/* Source Header */}
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <div className={`p-2 rounded ${source.bgColor}`}>
                            <IconComponent className={`w-5 h-5 ${source.color}`} />
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <h3 className={`font-mono font-bold ${source.color}`}>
                                {source.name}
                              </h3>
                              {source.critical && (
                                <span className="px-2 py-0.5 bg-red-900/50 text-red-400 text-xs font-mono rounded">
                                  CRITIQUE
                                </span>
                              )}
                              {isConnected && (
                                <span className="px-2 py-0.5 bg-green-900/50 text-green-400 text-xs font-mono rounded flex items-center gap-1">
                                  <CheckCircle2 className="w-3 h-3" />
                                  CONNECTÉ
                                </span>
                              )}
                            </div>
                            <p className="font-mono text-xs text-white/80">{source.category}</p>
                          </div>
                        </div>
                        <a
                          href={source.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-2 text-white/80 hover:text-white hover:bg-white/10 rounded"
                        >
                          <ExternalLink className="w-4 h-4" />
                        </a>
                      </div>

                      {/* Description */}
                      <p className="font-mono text-sm text-white/90 mb-2">
                        {source.description}
                      </p>
                      <p className="font-mono text-xs text-white/80 mb-4">
                        <strong>Accès actuel:</strong> {source.accessLevel}
                      </p>

                      {/* Credentials Form */}
                      <div className="grid md:grid-cols-2 gap-3">
                        <div>
                          <label className="font-mono text-xs text-white/80 mb-1 block">
                            Identifiant / Email
                          </label>
                          <input
                            type="text"
                            value={cred.username || ''}
                            onChange={e => handleInputChange(source.id, 'username', e.target.value)}
                            placeholder="votre@email.com"
                            className="w-full px-3 py-2 bg-black/50 border border-marine-slate/50 rounded font-mono text-sm text-white placeholder:text-white/30 focus:border-marine-cyan focus:outline-none"
                          />
                        </div>
                        <div>
                          <label className="font-mono text-xs text-white/80 mb-1 block">
                            Mot de passe
                          </label>
                          <div className="relative">
                            <input
                              type={showPassword[source.id] ? 'text' : 'password'}
                              value={cred.password || ''}
                              onChange={e => handleInputChange(source.id, 'password', e.target.value)}
                              placeholder="••••••••"
                              className="w-full px-3 py-2 pr-10 bg-black/50 border border-marine-slate/50 rounded font-mono text-sm text-white placeholder:text-white/30 focus:border-marine-cyan focus:outline-none"
                            />
                            <button
                              type="button"
                              onClick={() => setShowPassword(prev => ({ ...prev, [source.id]: !prev[source.id] }))}
                              className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-white/80 hover:text-white"
                            >
                              {showPassword[source.id] ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                            </button>
                          </div>
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="flex items-center justify-end gap-2 mt-3">
                        {isConnected && (
                          <button
                            onClick={() => handleDeleteCredential(source.id)}
                            className="px-3 py-1.5 bg-red-900/30 border border-red-500/50 text-red-400 font-mono text-sm flex items-center gap-2 hover:bg-red-900/50 rounded"
                          >
                            <Trash2 className="w-4 h-4" />
                            Supprimer
                          </button>
                        )}
                        <button
                          onClick={() => handleSaveCredential(source.id)}
                          disabled={isSaving[source.id]}
                          className={`px-4 py-1.5 border font-mono text-sm flex items-center gap-2 rounded disabled:opacity-50 ${
                            isConnected 
                              ? 'bg-marine-cyan/20 border-marine-cyan/50 text-marine-cyan hover:bg-marine-cyan/30'
                              : 'bg-marine-amber/20 border-marine-amber/50 text-marine-amber hover:bg-marine-amber/30'
                          }`}
                        >
                          {isSaving[source.id] ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Save className="w-4 h-4" />
                          )}
                          {isConnected ? 'Mettre à jour' : 'Connecter'}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="px-6 py-3 border-t border-marine-slate/30 bg-black/30">
            <p className="font-mono text-white/40 text-xs text-center">
              {savedSources.length} source(s) connectée(s) sur {PREMIUM_SOURCES.length}
            </p>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default PremiumSourcesPanel;
